import {
  ClipboardList,
  Database,
  Layers,
  Palette,
  RadioTower,
  Users,
  Waves,
  Shield,
  LayoutGrid,
  Menu,
  Folder,
  FileText,
  Settings,
  Bell,
} from 'lucide-react';

import { env } from '@/config/env';
import { PATHS } from '@/config/paths';

import type { OpsNavItem } from './opsNav.types';

/** Ordered ops area routes; used by sidebar and router metadata. */
export const OPS_NAV_ITEMS: OpsNavItem[] = [
  {
    key: 'audit',
    path: PATHS.OPS_AUDIT,
    labelKey: 'ops.nav.audit',
    icon: ClipboardList,
    menuPermissionKey: 'audit_log_view',
    requiredPermissions: ['ops:audit'],
  },
  {
    key: 'jobs',
    path: PATHS.OPS_JOBS,
    labelKey: 'ops.nav.jobs',
    icon: Layers,
    requiredPermissions: ['ops:jobs'],
  },
  {
    key: 'netcdf',
    path: PATHS.DATA_UPLOAD,
    labelKey: 'ops.nav.netcdf',
    icon: Database,
    menuPermissionKey: 'data_upload',
    requiredPermissions: ['ops:jobs'],
  },
  {
    key: 'reports',
    path: PATHS.OPS_REPORTS,
    labelKey: 'ops.nav.reports',
    icon: FileText,
    menuPermissionKey: 'exec_reports',
    requiredPermissions: ['reports:view'],
  },
  {
    key: 'users',
    path: PATHS.OPS_USERS,
    labelKey: 'ops.nav.users',
    icon: Users,
    menuPermissionKey: 'user_management',
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:users'],
  },
  {
    key: 'basins',
    path: PATHS.OPS_BASINS,
    labelKey: 'ops.nav.basins',
    icon: Waves,
    menuPermissionKey: 'iot_station_management',
    requiredPermissions: ['ops:stations'],
  },
  {
    key: 'stations',
    path: PATHS.OPS_STATIONS,
    labelKey: 'ops.nav.stations',
    icon: RadioTower,
    menuPermissionKey: 'iot_station_management',
    requiredPermissions: ['ops:stations'],
  },
  {
    key: 'sensor-types',
    path: PATHS.OPS_SENSOR_TYPES,
    labelKey: 'ops.nav.sensorTypes',
    icon: Palette,
    menuPermissionKey: 'iot_station_management',
    requiredPermissions: ['ops:stations'],
  },
  {
    key: 'roles',
    path: PATHS.OPS_ROLES,
    labelKey: 'ops.nav.roles',
    icon: Shield,
    menuPermissionKey: 'user_management',
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:roles'],
  },
  {
    key: 'permission-matrix',
    path: PATHS.OPS_FEATURE_MATRIX,
    labelKey: 'ops.nav.featureMatrix',
    icon: LayoutGrid,
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:roles'],
  },
  {
    key: 'menus',
    path: PATHS.OPS_MENUS,
    labelKey: 'ops.nav.menus',
    icon: Menu,
    menuPermissionKey: 'user_management',
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:menus'],
  },
  {
    key: 'catalog',
    path: PATHS.OPS_CATALOG,
    labelKey: 'ops.nav.catalog',
    icon: Folder,
    menuPermissionKey: 'system_config',
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:catalog'],
  },
  {
    key: 'system-config',
    path: PATHS.OPS_SYSTEM_CONFIG,
    labelKey: 'ops.nav.systemConfig',
    icon: Settings,
    menuPermissionKey: 'system_config',
    requiredPermissions: ['admin:system-config'],
  },
  {
    key: 'alert-settings',
    path: PATHS.OPS_ALERT_SETTINGS,
    labelKey: 'ops.nav.alertSettings',
    icon: Bell,
    menuPermissionKey: 'alert_threshold_config',
    requiredPermissions: ['ops:alert-settings'],
  },
];

/** Mục vẫn là placeholder trên FE — mặc định ẩn khỏi sidebar (route trực tiếp vẫn qua RequireOpsRoute). */
const PLACEHOLDER_OPS_NAV_KEYS = new Set<string>(['system-config', 'alert-settings']);

/** Tạm ẩn khỏi sidebar / merge (menu động BE lọc thêm trong `opsMenuPaths.filterOpsSidebarHiddenPaths`). */
const TEMPORARILY_HIDDEN_OPS_NAV_KEYS = new Set<string>(['menus']);

/**
 * Danh sách mục dùng cho sidebar Ops và nút Vận hành trên header.
 * Khi {@link env.OPS_SHOW_PLACEHOLDER_NAV} là false, bỏ các mục placeholder.
 */
export function getOpsNavItemsForMenu(): OpsNavItem[] {
  const withoutTemp = OPS_NAV_ITEMS.filter((i) => !TEMPORARILY_HIDDEN_OPS_NAV_KEYS.has(i.key));
  if (env.OPS_SHOW_PLACEHOLDER_NAV) return withoutTemp;
  return withoutTemp.filter((i) => !PLACEHOLDER_OPS_NAV_KEYS.has(i.key));
}
